import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { groupResultsByBatch } from '../../../components/ListingScore/bulk_listing_score/ListingScoreResultHistory';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fetches single + bulk listing-score history and merges them into date-sorted
// items ({type: 'batch'|'single', key, date, batch|result}). Shared by the
// Manage page's merged grid and the listing-score filter view.
const useListingScoreHistory = (token) => {
  const [results, setResults] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bulkError, setBulkError] = useState(null);

  const fetchSingleHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/listing_score/single-history`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.data?.success && Array.isArray(response.data.results)) {
        setResults(response.data.results);
      } else {
        setResults([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load listing scores');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchBulkHistory = useCallback(async () => {
    if (!token) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/listing_score/all-scores`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.data?.success && Array.isArray(response.data.results)) {
        const sorted = [...response.data.results].sort((a, b) => {
          const da = a.completed_at ? new Date(a.completed_at) : new Date(0);
          const db = b.completed_at ? new Date(b.completed_at) : new Date(0);
          return db - da;
        });
        setBulkResults(sorted);
      } else {
        setBulkResults([]);
      }
    } catch (err) {
      setBulkError(err.response?.data?.error || err.message || 'Failed to load bulk listing scores');
      setBulkResults([]);
    } finally {
      setBulkLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSingleHistory(); }, [fetchSingleHistory]);
  useEffect(() => { fetchBulkHistory(); }, [fetchBulkHistory]);

  const combinedItems = useMemo(() => {
    const batchItems = groupResultsByBatch(bulkResults).map((batch) => ({
      type: 'batch',
      key: `batch-${batch.task_id}`,
      date: batch.completed_at ? new Date(batch.completed_at) : new Date(0),
      batch,
    }));
    // single-history returns one row per run; keep only the newest per ASIN so
    // re-analysing the same ASIN doesn't show duplicate cards. `results` is
    // already newest-first, so the first occurrence per ASIN is the latest.
    const seenAsins = new Set();
    const singleItems = [];
    results.forEach((result, index) => {
      const asin = result.asin;
      if (!asin || seenAsins.has(asin)) return;
      seenAsins.add(asin);
      singleItems.push({
        type: 'single',
        key: `single-${asin}-${result.created_at || index}`,
        date: result.created_at ? new Date(result.created_at) : new Date(0),
        result,
      });
    });
    return [...batchItems, ...singleItems].sort((a, b) => b.date - a.date);
  }, [bulkResults, results]);

  return {
    loading: loading || bulkLoading,
    error: error || bulkError,
    combinedItems,
  };
};

export default useListingScoreHistory;
